/**
 *
 * PlatformTransferLayout
 *
 */

import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useAuth } from "react-oidc-context";
import { stylize } from "@envestnet/envreact-component-library";
import { AppError } from "components/AppError";
import PageLoadingComponent from "components/PageLoadingComponent";
import { decodeAccessTokenClaims } from "utils/decodeAccessTokenClaims";
import history from "utils/history";
import { parseResourceId } from "utils/parseResourceId";
import {
  getPlatformTransferPath,
  PLATFORM_TRANSFER_PATH,
} from "utils/platformTransfer";
import withIntl from "utils/withIntl";
import storage from "../../persistance";

import styles, { Styles } from "./styles";
import { TransferContext } from "./types";

const RESOURCE_ID_KEY = "resourceId";
const DASHBOARD_PATH = "/dashboard";

interface InjectedProps extends Styles {
  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  /** Sets the access token on the API handler (global `setToken` action) */
  setToken: (token: string) => void;

  /** Stores platform, application, cidpId and partnerId for the access check */
  setTransferContext: (context: TransferContext) => void;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
}

export interface PlatformTransferLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & PlatformTransferLayoutProps;

/** `resourceId` from the query string when present, from storage otherwise. */
const readResourceId = (): string | null => {
  const fromQuery = new URLSearchParams(window.location.search).get(
    RESOURCE_ID_KEY
  );
  return fromQuery !== null ? fromQuery : storage.getItem(RESOURCE_ID_KEY);
};

const fallbackToDashboard = () => {
  storage.removeItem(RESOURCE_ID_KEY);
  history.replace(DASHBOARD_PATH);
};

const PlatformTransferLayout = (props: Props) => {
  const { classes, style, className, innerRef, setToken, setTransferContext } =
    props;

  const auth = useAuth();
  const signinStarted = useRef(false);
  const handedOff = useRef(false);
  const [signinFailed, setSigninFailed] = useState(false);

  const hasError = !!auth.error || signinFailed;

  useEffect(() => {
    // Wait for a pending callback or navigation before deciding anything.
    if (auth.isLoading || auth.activeNavigator) {
      return;
    }

    if (auth.error) {
      // Drop the stale sign-in parameters (replace, path unchanged) so a reload signs in again.
      if (window.location.search) {
        history.replace(PLATFORM_TRANSFER_PATH);
      }
      return;
    }

    if (signinFailed || handedOff.current || signinStarted.current) {
      return;
    }

    const resourceId = readResourceId();
    const parsedResourceId = parseResourceId(resourceId);
    if (!resourceId || !parsedResourceId) {
      fallbackToDashboard();
      return;
    }

    if (!auth.isAuthenticated) {
      signinStarted.current = true;
      // The failed callback never stores it, so keep it for a reload.
      storage.setItem(RESOURCE_ID_KEY, resourceId);
      auth
        .signinRedirect({
          redirect_uri: window.location.origin + getPlatformTransferPath(),
          state: resourceId,
          redirectMethod: "replace",
        })
        .catch(() => setSigninFailed(true));
      return;
    }

    const accessToken = auth.user?.access_token ?? "";
    const { cidpId, partnerId } = decodeAccessTokenClaims(accessToken);
    if (!cidpId) {
      fallbackToDashboard();
      return;
    }

    handedOff.current = true;
    setToken(accessToken);
    setTransferContext({
      platform: parsedResourceId.platform,
      application: parsedResourceId.application,
      cidpId,
      partnerId,
    });
  }, [
    auth.isLoading,
    auth.activeNavigator,
    auth.error,
    auth.isAuthenticated,
    signinFailed,
  ]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
      data-testid="platform-transfer"
    >
      {hasError ? <AppError /> : <PageLoadingComponent isFullPage />}
    </div>
  );
};

PlatformTransferLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(PlatformTransferLayout)
);
